import React from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Paper,
  Typography,
} from "@mui/material";
import {
  LocalFlorist as LeafIcon,
  ArrowForward as ArrowForwardIcon,
  Park as SproutIcon,
  Forest as TreePineIcon,
} from "@mui/icons-material";
import FarmappLogo from "../../assets/logo/FarmAppLogo.png";
import SimpleHeader from "../../components/SimpleHeader";

export default function BiocharLanding() {
  const navigate = useNavigate();

  const handleNavigation = (path) => {
    navigate(path);
  };

  return (
    <>
      <Helmet>
        <title>Biochar Landing - Farmapp</title>
      </Helmet>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
          marginBottom: "-64px",
        }}
      >
        {/* Header */}

        <SimpleHeader />

        <Box component="main" sx={{ flexGrow: 1 }}>
          <Container maxWidth="lg" sx={{ py: { xs: 6, md: 12 } }}>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 4 }}>
                  <Typography
                    variant="h2"
                    gutterBottom
                    sx={{
                      fontWeight: "bold",
                      mb: 2,
                      fontSize: { xs: "2.5rem", md: "3.5rem" },
                    }}
                  >
                    Revolutionize Soil Health with{" "}
                    <Box component="span" sx={{ color: "primary.main" }}>
                      Biochar
                    </Box>
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ color: "text.secondary", mb: 4 }}
                  >
                    Discover the power of biochar in sustainable agriculture.
                    Our innovative solution enhances soil fertility, increases
                    crop yields, and reduces environmental impact.
                  </Typography>
                  <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={() => handleNavigation("/biochar-interest-form")}
                      endIcon={<ArrowForwardIcon />}
                      sx={{ color: "white" }}
                    >
                      Get Started
                    </Button>
                    <Button
                      variant="outlined"
                      size="large"
                      onClick={() => window.open("https://asqi.in/biochar/")}
                    >
                      Know more
                    </Button>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={3}
                  sx={{
                    position: "relative",
                    borderRadius: 2,
                    overflow: "hidden",
                    aspectRatio: "16/9",
                  }}
                >
                  <iframe
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                    }}
                    src="https://www.youtube.com/embed/B8eSy_vxvFE?controls=0&rel=0&modestbranding=1&showinfo=0"
                    title="Biochar Transformation"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </Paper>
              </Grid>
            </Grid>
          </Container>

          {/* Benefits Section */}
          <Container maxWidth="lg" sx={{ py: { xs: 6, md: 12 } }} id="benefits">
            <Box sx={{ textAlign: "center", mb: 6 }}>
              <Typography variant="h3" gutterBottom fontWeight="bold">
                Benefits of Biochar
              </Typography>
              <Typography
                variant="h6"
                sx={{ color: "text.secondary", maxWidth: 700, mx: "auto" }}
              >
                Discover how biochar can transform your agricultural practices
                and contribute to a sustainable future.
              </Typography>
            </Box>
            <Grid container spacing={4}>
              {[
                {
                  icon: (
                    <LeafIcon sx={{ fontSize: 48, color: "primary.main" }} />
                  ),
                  title: "Enhanced Soil Fertility",
                  description:
                    "Improves soil structure and increases nutrient retention for better plant growth.",
                },
                {
                  icon: (
                    <SproutIcon sx={{ fontSize: 48, color: "primary.main" }} />
                  ),
                  title: "Higher Crop Yields",
                  description:
                    "Increases agricultural productivity through improved soil conditions.",
                },
                {
                  icon: (
                    <TreePineIcon
                      sx={{ fontSize: 48, color: "primary.main" }}
                    />
                  ),
                  title: "Carbon Sequestration",
                  description:
                    "Helps combat climate change by storing carbon in the soil for centuries.",
                },
              ].map((benefit, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Card sx={{ height: "100%" }}>
                    <CardContent sx={{ textAlign: "center", pt: 3 }}>
                      {benefit.icon}
                      <Typography
                        variant="h5"
                        sx={{ my: 2, fontWeight: "bold" }}
                      >
                        {benefit.title}
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        {benefit.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>

          {/* CTA Section */}
          <Paper sx={{ bgcolor: "grey.50", boxShadow: "none" }}>
            <Container maxWidth="lg" sx={{ py: { xs: 6, md: 12 } }}>
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="h3" gutterBottom fontWeight="bold">
                  Ready to Transform Your Soil?
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    color: "text.secondary",
                    maxWidth: 600,
                    mx: "auto",
                    mb: 4,
                  }}
                >
                  Join ASQI sustainable agriculture revolution today and
                  experience the benefits of biochar firsthand.
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => handleNavigation("/biochar-interest-form")}
                  sx={{ color: "white" }}
                >
                  Get Started with Biochar
                </Button>
              </Box>
            </Container>
          </Paper>
        </Box>

        {/* Footer */}
        <Box
          component="footer"
          sx={{
            py: 3,
            borderTop: 1,
            borderColor: "divider",
          }}
        >
          <Container maxWidth="lg">
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <img
                  src={FarmappLogo}
                  alt="Farmapp Logo"
                  style={{ height: "60px", width: "auto" }}
                />
              </Box>
              <Typography variant="body2" color="text.secondary">
                © {new Date().getFullYear()} ASQI All rights reserved.
              </Typography>
            </Box>
          </Container>
        </Box>
      </Box>
    </>
  );
}
